import {
    useCallback,
    useContext,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    LogOut,
    PanelLeft,
    Search,
    Settings,
    ShoppingCart,
} from 'lucide-react';
import { toast } from 'sonner';

import { Breadcrumbs } from './Breadcrumbs';
import { NotificationsMenu } from './NotificationsMenu';

import { ThemeToggle } from '@/components/ThemeToggle';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Avatar,
    AvatarFallback,
} from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import AuthContext from '@/contexts/auth';
import { baseRequest } from '@/lib/base';
import {
    extractCart,
    fetchActiveCart,
} from '@/pages/Cart/api';

interface NavbarProps {
    onToggleSidebar?: () => void;
}

function Navbar({ onToggleSidebar }: NavbarProps) {
    const { state, logout: userLogout } = useContext(
        AuthContext,
    );
    const navigate = useNavigate();
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    const { data: cartRes } = useQuery({
        queryKey: ['cart'],
        queryFn: fetchActiveCart,
    });
    const cartCount = cartRes?.ok
        ? (extractCart(cartRes.data)?.items?.length ?? 0)
        : 0;

    const handleLogout = useCallback(async () => {
        try {
            const response = await baseRequest({
                url: '/system/auth/logout/',
                method: 'GET',
            });
            if (response?.data?.status) {
                userLogout();
                toast.success('Logout successful');
            } else {
                toast.error(
                    response?.data?.data?.logout?.error
                    || 'Logout failed',
                );
            }
        } catch (error: unknown) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : 'An error occurred during logout',
            );
        }
    }, [userLogout]);

    return (
        <header className="bg-background border-b border-border sticky top-0 z-30">
            <div className="h-[60px] flex items-center px-4 md:px-6 gap-2">
                {/* Left: sidebar trigger + breadcrumb trail */}
                <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={onToggleSidebar}
                    className="-ml-1 text-muted-foreground hover:text-foreground"
                    aria-label="Toggle sidebar"
                >
                    <PanelLeft className="h-4 w-4" />
                </Button>
                <Separator
                    orientation="vertical"
                    className="mr-1 !h-4"
                />
                <Breadcrumbs />

                {/* Right: search + actions */}
                <div className="ml-auto flex items-center gap-1.5">
                    <div className="relative hidden sm:block">
                        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                            type="search"
                            placeholder="Search..."
                            aria-label="Search"
                            className="h-9 w-44 lg:w-64 rounded-md border border-border bg-card pl-9 pr-3 text-sm placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30 focus-visible:border-ring transition-[color,box-shadow]"
                        />
                    </div>

                    <ThemeToggle />
                    <NotificationsMenu />

                    <Button
                        variant="ghost"
                        size="icon"
                        className="relative text-muted-foreground hover:text-foreground"
                        onClick={() => navigate('/cart')}
                        aria-label="Cart"
                    >
                        <ShoppingCart className="h-5 w-5" />
                        {cartCount > 0 && (
                            <span className="absolute top-1 right-1 h-4 min-w-4 px-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-none flex items-center justify-center ring-2 ring-background">
                                {cartCount}
                            </span>
                        )}
                    </Button>

                    <Separator
                        orientation="vertical"
                        className="mx-1 !h-4"
                    />

                    {/* User Menu */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                className="relative h-9 w-9 rounded-full p-0"
                            >
                                <Avatar className="h-9 w-9">
                                    <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                                        {(state?.businessName || state?.name)
                                            ?.charAt(0)
                                            .toUpperCase()
                                            || 'U'}
                                    </AvatarFallback>
                                </Avatar>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            className="w-56 bg-card border-border/60 shadow-lg"
                            align="end"
                        >
                            <div className="px-3 py-2 border-b border-border">
                                <p className="text-sm font-semibold">
                                    {state?.businessName || state?.name || 'User'}
                                </p>
                            </div>
                            <DropdownMenuItem
                                className="cursor-pointer"
                                onClick={() => navigate(
                                    '/settings',
                                )}
                            >
                                <Settings className="mr-2 h-4 w-4" />
                                <span>Settings</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                className="cursor-pointer hover:bg-destructive/10 hover:text-destructive focus:bg-destructive/10 focus:text-destructive"
                                onSelect={(e) => {
                                    e.preventDefault();
                                    setLogoutConfirmOpen(true);
                                }}
                            >
                                <LogOut className="mr-2 h-4 w-4" />
                                <span>Log out</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <AlertDialog
                open={logoutConfirmOpen}
                onOpenChange={setLogoutConfirmOpen}
            >
                <AlertDialogContent className="rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Log out?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to log out? You will need
                            to sign in again to continue.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleLogout}
                            className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Log out
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

        </header>
    );
}

export default Navbar;
